import { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TilesPanel from "./TilesPanel";
import FieldPanel from "./FieldPanel";
import AddAreaPanel from "./AddAreaPanel";
import { setCurrentWorkspace } from "../reducers/MapSlice";
import { NARROW_SCREEN, PHONE_SCREEN, THIN_SCROLLBAR } from "../utils/layout";

export default function PanelSwitcher({ collapsed, onCollapse }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const drawingMode = useSelector((state) => state.mapState.drawingMode);
  const workspaces = useSelector((state) => state.mapState.workspaces);
  const currentWorkspace = useSelector(
    (state) => state.mapState.currentWorkspace,
  );
  const tilesLimits = useSelector((state) => state.mapState.tilesLimits);
  const tiles = workspaces[currentWorkspace];

  const [activePanel, setActivePanel] = useState("field");
  const phone = useMediaQuery(PHONE_SCREEN);
  // The add/edit form has no tabs to fold into, so it always shows.
  const isCollapsed = collapsed && !drawingMode;

  const workspacesArray = useMemo(() => {
    return Array.from({ length: tilesLimits.workspaces }, (_, i) => i + 1);
  }, [tilesLimits.workspaces]);

  const handleWorkspaceChange = (_, newWorkspace) => {
    if (newWorkspace !== null) {
      dispatch(setCurrentWorkspace(newWorkspace));
    }
  };

  return (
    <Box
      className="map-overlay map-overlay--right"
      sx={{
        position: "fixed",
        top: "20px",
        bottom: "auto",
        right: "20px",
        left: "auto",
        display: "flex",
        flexDirection: "row",
        gap: 2,
        alignItems: "flex-start",
        zIndex: 1000,
        width: "auto",
        pointerEvents: "none",
        [`@media ${PHONE_SCREEN}`]: {
          top: "auto",
          bottom: "24px",
          left: "12px",
          right: "12px",
          zIndex: 1001, // opens over the map bar
        },
      }}
    >
      <Box
        sx={{
          // Shrinks on narrow windows so it doesn't cover the left panel.
          width: "clamp(280px, calc(100vw - 400px), 350px)",
          pointerEvents: "auto",
          [`@media ${PHONE_SCREEN}`]: { width: "100%" },
        }}
      >
        {/* Integrated Panel Container */}
        <Box
          className="glass-panel"
          sx={{
            p: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            // Grows to the bottom of the screen, keeping the top's 20px margin.
            maxHeight: "calc(100vh - 40px)",
            [`@media ${NARROW_SCREEN}`]: { maxHeight: "calc(100vh - 124px)" },
            [`@media ${PHONE_SCREEN}`]: { maxHeight: "60vh" },
            boxSizing: "border-box",
          }}
        >
          {/* Tab Header */}
          {!drawingMode && (
            <Box
              sx={{
                display: "flex",
                borderBottom: "1px solid rgba(0,0,0,0.05)",
                backgroundColor: "rgba(255,255,255,0.3)",
              }}
            >
              {["field", "tiles"].map((panel) => (
                <Box
                  key={panel}
                  onClick={() => {
                    setActivePanel(panel);
                    onCollapse(false);
                  }}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    transition: "all 0.2s ease",
                    backgroundColor:
                      activePanel === panel
                        ? "rgba(var(--primary-rgb), 0.08)"
                        : "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(var(--primary-rgb), 0.04)",
                    },
                  }}
                >
                  <Typography
                    variant="button"
                    sx={{
                      fontWeight: activePanel === panel ? 800 : 500,
                      color:
                        activePanel === panel
                          ? "primary.main"
                          : "text.secondary",
                      fontSize: "0.75rem",
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                    }}
                  >
                    {panel === "field"
                      ? t("map.panels.fieldDetails")
                      : t("map.panels.layers")}
                  </Typography>
                  {activePanel === panel && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        backgroundColor: "primary.main",
                        borderRadius: "3px 3px 0 0",
                      }}
                    />
                  )}
                </Box>
              ))}
              <Tooltip
                title={
                  collapsed ? t("map.expandPanel") : t("map.collapsePanel")
                }
              >
                <IconButton
                  size="small"
                  onClick={() => onCollapse(!collapsed)}
                  aria-label={
                    collapsed ? t("map.expandPanel") : t("map.collapsePanel")
                  }
                  aria-expanded={!collapsed}
                  sx={{ alignSelf: "center", mx: 0.5 }}
                >
                  {/* On phones opens upwards. */}
                  {collapsed === phone ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Collapse
            in={!isCollapsed}
            sx={{
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              },
            }}
          >
            {/* Panel content area */}
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflowY: "auto",
                ...THIN_SCROLLBAR,
              }}
            >
              {drawingMode ? (
                <AddAreaPanel />
              ) : (
                <>
                  {activePanel === "tiles" && (
                    <Box sx={{ p: 2 }}>
                      {tiles && tiles.length > 0 ? (
                        <TilesPanel tiles={tiles} />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: "center",
                            py: 4,
                            color: "text.secondary",
                          }}
                        >
                          {t("map.panels.noLayers")}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {activePanel === "field" && <FieldPanel />}
                </>
              )}
            </Box>

            {/* Workspace Footer (only for tiles) */}
            {activePanel === "tiles" && !drawingMode && (
              <Box
                sx={{
                  p: 1.5,
                  borderTop: "1px solid rgba(0,0,0,0.05)",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Tooltip title={t("map.panels.workspaceHelp")}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "text.secondary",
                      cursor: "help",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 800, textTransform: "uppercase" }}
                    >
                      {t("map.panels.workspace")}
                    </Typography>
                    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Tooltip>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {workspacesArray.map((wsNum) => {
                    const count = workspaces[wsNum].length;
                    const label = t("map.panels.workspaceLabel", {
                      number: wsNum,
                      content:
                        count === 0
                          ? t("map.panels.empty")
                          : t("map.panels.layerCount", { count }),
                    });
                    return (
                      <Tooltip key={wsNum} title={label}>
                        <ButtonBase
                          aria-label={label}
                          aria-pressed={currentWorkspace === wsNum}
                          onClick={() => handleWorkspaceChange(null, wsNum)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: 700,
                            cursor: "pointer",
                            transition: "all 0.2s",
                            backgroundColor:
                              currentWorkspace === wsNum
                                ? "primary.main"
                                : "rgba(0,0,0,0.05)",
                            color:
                              currentWorkspace === wsNum
                                ? "white"
                                : "text.primary",
                            "&:hover": {
                              backgroundColor:
                                currentWorkspace === wsNum
                                  ? "primary.dark"
                                  : "rgba(0,0,0,0.1)",
                            },
                            position: "relative",
                          }}
                        >
                          {wsNum}
                          {count > 0 && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: -4,
                                right: -4,
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: "secondary.main",
                                border: "1.5px solid white",
                              }}
                            />
                          )}
                        </ButtonBase>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}
